// How a bed's location is put into words, in one place.
//
// The unit, room and bed descriptions all come from the SAME PCC column per
// field - the census renames UnitDescription to `unit_desc`, the risk pipeline
// renames it to `unit` - and they are free text typed per facility. Some sites
// enter "4", others enter "Unit 4". Prefixing unconditionally printed
// "Unit Unit 4" on the bed board's unit cards, in the discharge popup's title,
// on the Discharge Plan and High Risk screens, in both PDFs, and in the text
// their searches matched against.
//
// Four call sites had their own copy of this before it was worth sharing.

/** The three nouns this module knows. A closed set, so the regex table below
 *  is built once and a caller cannot hand in a string that is not a noun. */
export type LocationNoun = 'Unit' | 'Room' | 'Bed';

// "Already carries the noun" = the value starts with the noun and the noun is
// not the beginning of a longer word. The lookahead is the word boundary:
// any letter or digit in any script continues the word ("Unitas", "Unité"
// both still get a prefix), anything else ends it ("Unit 4", "Unit-4",
// "Unit_4", "Unit: 4" are all left alone). `\b` was not enough - it treats
// "_" as a word character and "é" as a word break, the opposite of both.
const NOUN_RE: Record<LocationNoun, RegExp> = {
  Unit: /^unit(?![\p{L}\p{N}])/iu,
  Room: /^room(?![\p{L}\p{N}])/iu,
  Bed: /^bed(?![\p{L}\p{N}])/iu,
};

/** `noun` prepended to `value`, unless the value already leads with it.
 *  Case-insensitive, and the value's own casing is kept - "UNIT 4" is what the
 *  facility typed. Surrounding whitespace is dropped: a leading space is the
 *  commonest defect in hand-typed descriptions, and it must not defeat the
 *  check or survive into a title. An empty value is just the noun. */
export function labelled(noun: LocationNoun, value: string): string {
  const text = (value ?? '').trim();
  if (!text) return noun;
  return NOUN_RE[noun].test(text) ? text : `${noun} ${text}`;
}

/** "Unit North · Room 101 · Bed A" - only the parts that exist. */
export function locationText(unit: string, room: string, bed: string): string {
  const parts: string[] = [];
  if (unit) parts.push(labelled('Unit', unit));
  if (room) parts.push(labelled('Room', room));
  if (bed) parts.push(labelled('Bed', bed));
  return parts.join(' · ');
}
